/**
 * How loud the far end is, applied to the elements the SDK plays them through.
 *
 * The Voice SDK renders remote audio into `<audio>` elements it creates itself,
 * one per active output device, and it hands each one out as it is built:
 * `call.on("audio", el => ...)`. That event is the whole mechanism here. It is
 * public API, it fires again when the operator switches speakers mid-call (the
 * SDK builds a new element for the new sink), and setting `volume` on it is a
 * property assignment with nothing behind it that can fail.
 *
 * DELIBERATELY NOT A GAIN NODE. Twilio also accepts a REMOTE audio processor,
 * which would allow going above 100% - and would put a `MediaStreamDestination`
 * in the path of every incoming voice, where a suspended `AudioContext` means
 * an operator who cannot hear anybody and has no way to tell why. The ceiling
 * is worth more than the headroom: `AUDIO_VOLUME_MAX.output` is 1 for this
 * reason, and the machine's own volume control is the way past it.
 *
 * The element for a browser with no `setSinkId` never arrives. The SDK's
 * fallback path builds that one with `document.createElement` and does not
 * announce it, so this module simply has nothing to hold - the same browsers
 * where `AudioPicker` already says it cannot route audio per device, and where
 * the speaker slider is therefore not drawn.
 */
import { clampVolume, readAudioVolume } from "./volume";

/**
 * Every element the running call is being played through.
 *
 * A `Set` rather than a single element because a call can be played out of
 * several sinks at once, and because the master element stays live while a
 * replacement is being built.
 */
const elements = new Set<HTMLMediaElement>();

/** What was last asked for. Seeded from storage so the first call is right. */
let level = 1;
let known = false;

function current(): number {
  if (!known) {
    level = readAudioVolume().output;
    known = true;
  }
  return level;
}

/** Assignment can still throw on a value out of range, so it is clamped first. */
function paint(el: HTMLMediaElement, value: number): void {
  try {
    el.volume = value;
  } catch {
    // A detached element, or a browser refusing the assignment. There is
    // nothing an operator could do about either and nothing worth saying.
  }
}

/**
 * A new element the call is coming out of.
 *
 * Called straight from the SDK's `audio` event. The level is applied here
 * rather than waiting for the next slider move, because the common case is a
 * call starting at a level chosen days ago.
 */
export function trackRemoteAudio(el: HTMLMediaElement): void {
  elements.add(el);
  paint(el, current());
}

/**
 * Turn the far end up or down, now, on every element carrying it.
 *
 * Also remembered for the elements that do not exist yet: the next call starts
 * where this one was left.
 */
export function setSpeakerLevel(value: number): void {
  level = clampVolume("output", value);
  known = true;
  for (const el of elements) {
    // An element the SDK has finished with keeps no source. Dropped here rather
    // than in a listener of our own, so a long call that switched speakers a
    // dozen times does not accumulate a dozen dead elements.
    if (!el.srcObject) {
      elements.delete(el);
      continue;
    }
    paint(el, level);
  }
}

/** The call is over: let the elements go. The level itself is kept. */
export function forgetRemoteAudio(): void {
  elements.clear();
}

/** How many elements the current call is being played through. */
export const remoteAudioCount = (): number => elements.size;
